import React, { useRef, useState, useEffect } from 'react';

interface OtpInputProps {
    length?: number;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({ length = 6, value, onChange, disabled = false }) => {
    // Create an array of refs using useRef to persist the array
    // Initialize with nulls, we'll assign them in the callback ref
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Initialize refs array based on length
    useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);


    const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (isNaN(Number(val))) return; // Only numbers allowed

        const newValue = value.split('');
        // Ensure array is correct length
        while (newValue.length < length) newValue.push('');

        // Take the last character if multiple typed (though typical usage is one char)
        newValue[index] = val.substring(val.length - 1);

        const newCode = newValue.join('').substring(0, length);
        onChange(newCode);

        // Focus next input
        if (val && index < length - 1 && inputRefs.current[index + 1]) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        // Move to previous on Backspace if empty
        if (e.key === 'Backspace') {
            if (!value[index] && index > 0 && inputRefs.current[index - 1]) {
                inputRefs.current[index - 1]?.focus();
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain').slice(0, length);
        if (!/^\d+$/.test(pastedData)) return; // Only numbers

        onChange(pastedData);

        // Focus necessary input
        const nextFocusIndex = Math.min(pastedData.length, length - 1);
        if (inputRefs.current[nextFocusIndex]) {
            inputRefs.current[nextFocusIndex]?.focus();
        }
    };

    // Helper to safely get char at index
    const getCharAt = (idx: number) => {
        return value[idx] || '';
    }

    return (
        <div className="flex justify-center gap-2">
            {Array.from({ length }, (_, index) => (
                <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={getCharAt(index)}
                    onChange={(e) => handleChange(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    disabled={disabled}
                    className="w-10 h-12 text-center text-xl font-bold bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    autoFocus={index === 0}
                />
            ))}
        </div>
    );
};

export default OtpInput;
